import type { ModalitaPartecipazione, WebinarPartecipante } from '@aulaviva/shared';
import { Alert, Button, EmptyState, PageHeading, WebinarCard, useToast } from '@aulaviva/ui';
import { CalendarClock } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMieiAttestati } from '@/api/documenti';
import { useConfermaWebinar, usePercorsi } from '@/api/percorsi';
import { useUtenteArea } from '@/layout/LayoutPartecipante';
import { formattaDataBreve, formattaIntervallo } from '@/lib/date';
import { comuni, documenti, percorsi as t, test as tt } from '@/testi';
import { DialogoModalita } from './componenti/DialogoModalita';
import { StatoConferma } from './componenti/StatoConferma';

function CardWebinar({ webinar, onConferma, attestatoUrl }: { webinar: WebinarPartecipante; onConferma: (w: WebinarPartecipante) => void; attestatoUrl?: string | null }) {
  const navigate = useNavigate();
  const confermato = webinar.conferma?.stato === 'confermato';
  const daConfermare = webinar.conferma?.stato === 'invitato';
  const azione = daConfermare ? (
    <Button variant="primary" onClick={() => onConferma(webinar)}>
      {t.azioni.conferma}
    </Button>
  ) : attestatoUrl ? (
    <Button variant="primary" onClick={() => window.open(attestatoUrl, '_blank', 'noopener')}>
      {documenti.attestati.scarica}
    </Button>
  ) : webinar.stato === 'test_aperto' && confermato ? (
    <Button variant="primary" onClick={() => navigate(`/webinar/${webinar.id}/test`)}>
      {tt.azioni.fai}
    </Button>
  ) : (webinar.eOggi || webinar.stato === 'in_diretta') && confermato && webinar.stato !== 'chiuso' ? (
    <Button variant="primary" onClick={() => navigate(`/webinar/${webinar.id}/aula`)}>
      {t.azioni.entra}
    </Button>
  ) : (
    <Button onClick={() => navigate(`/webinar/${webinar.id}`)}>{t.azioni.dettagli}</Button>
  );
  return (
    <WebinarCard
      dataOra={formattaIntervallo(webinar.inizioPrevisto, webinar.finePrevista)}
      titolo={webinar.titolo}
      relatori={t.relatori(webinar.relatori.map((r) => `${r.nome} ${r.cognome}`))}
      stato={<StatoConferma webinar={webinar} />}
      oggi={(webinar.eOggi || webinar.stato === 'in_diretta') && webinar.stato !== 'chiuso'}
      inDiretta={webinar.stato === 'in_diretta'}
      azione={azione}
    />
  );
}

export function IlMioPercorso() {
  const utente = useUtenteArea();
  const percorsi = usePercorsi();
  const attestati = useMieiAttestati();
  const toast = useToast();
  const [daConfermare, setDaConfermare] = useState<WebinarPartecipante | null>(null);
  const conferma = useConfermaWebinar(daConfermare?.id ?? '');

  async function confermaModalita(modalita: ModalitaPartecipazione) {
    try {
      await conferma.mutateAsync(modalita);
      toast.mostra({ variant: 'success', title: t.dettaglio.salvato, description: modalita === 'sala' ? t.dettaglio.confermatoSala : t.dettaglio.confermatoOnline });
    } catch (e) {
      toast.mostra({ variant: 'error', title: e instanceof Error ? e.message : comuni.erroreGenerico });
      throw e;
    }
  }

  return (
    <>
      <PageHeading subtitle={t.saluto(utente.nome)}>{t.titolo}</PageHeading>
      {percorsi.isPending && <p className="t-body text-ink-muted">{comuni.caricamento}</p>}
      {percorsi.isError && <Alert variant="error" title={percorsi.error.message} action={<Button onClick={() => percorsi.refetch()}>{comuni.riprova}</Button>} />}
      {percorsi.data && percorsi.data.length === 0 && (
        <EmptyState icon={CalendarClock} title={t.nessunPercorsoTitolo}>
          {t.nessunPercorsoTesto}
        </EmptyState>
      )}
      {percorsi.data?.map((p) => (
        <section key={p.id} aria-labelledby={`percorso-${p.id}`} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h2 id={`percorso-${p.id}`} className="t-title-2">
              {p.titolo}
            </h2>
            <p className="t-body-small text-ink-muted">{t.iscrittoDal(formattaDataBreve(p.iscrittoDal))}</p>
          </div>
          {p.webinar.length === 0 ? (
            <Alert variant="info" title={t.nessunWebinarTitolo}>
              {t.nessunWebinarTesto}
            </Alert>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {p.webinar.map((w) => (
                <CardWebinar key={w.id} webinar={w} onConferma={setDaConfermare} attestatoUrl={attestati.data?.find((a) => a.webinarId === w.id && a.stato === 'pronto' && !a.revocatoAt)?.url ?? null} />
              ))}
            </div>
          )}
          <p className="t-body-small text-ink-muted">{t.promemoriaInviti}</p>
        </section>
      ))}
      {daConfermare && (
        <DialogoModalita
          key={daConfermare.id}
          open
          onOpenChange={(o) => {
            if (!o) setDaConfermare(null);
          }}
          attuale={daConfermare.conferma?.modalita}
          onConferma={confermaModalita}
        />
      )}
    </>
  );
}
